import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { CookiesProvider } from 'react-cookie';
import { NavLink } from './types';
import Header from './components/organisms/Header';
import PromotionalBanner from './components/molecules/PromotionalBanner';
import HeroSection from './components/organisms/HeroSection';
import ProductCard from './components/organisms/ProductCard';
import Footer from './components/organisms/Footer';
import AdminLayout from './pages/admin/AdminLayout';
import CatalogPage from './pages/CatalogPage';
import CollectionDetailPage from './pages/CollectionDetailPage';
import ProductDetailPage from './pages/ProductDetailPage';
import CartPage from './pages/CartPage';
import CheckoutPage from './pages/CheckoutPage';
import ContactPage from './pages/ContactPage';
import PolicyPage from './pages/PolicyPage';
import FAQPage from './pages/FAQPage';
import PrivacyPage from './pages/PrivacyPage';
import CookiePreferencesPage from './pages/CookiePreferencesPage';
import { CartProvider, ToastProvider } from './contexts';
import { useStorefrontData } from './hooks/useStorefrontData';
import LogoSpinner from './components/atoms/LogoSpinner';
import CookieConsentBanner from './components/molecules/CookieConsentBanner';
import './styles/global.css';
import './App.css';

// Initialize Firebase
import './config/firebase';

// HomePage component that uses storefront data
const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { 
    getProductData, 
    getHeroImageSrc, 
    getPromotionalBannerData, 
    loading, 
    error 
  } = useStorefrontData();

  const navLinks: NavLink[] = [
    { text: 'Home', href: '/' },
    { text: 'Catalog', href: '/catalog' },
    { text: 'Contact', href: '/contact' }
  ];

  const productData = getProductData();
  const heroImageSrc = getHeroImageSrc();
  const bannerData = getPromotionalBannerData();

  const handleEmailSubmit = (email: string): void => {
    console.log('Email subscription:', email);
    // Handle email subscription logic here
  };

  const handleFollowShop = (): void => {
    console.log('Follow on shop clicked');
    // Handle follow shop logic here
  };

  const handleBannerClick = (): void => {
    navigate('/catalog');
  };

  // Show loading state
  if (loading) {
    return (
      <div className="App">
        <Header navLinks={navLinks} />
        <div className="loading-state">
          <LogoSpinner size="large" text="Loading homepage content..." />
        </div>
        <Footer 
          onEmailSubmit={handleEmailSubmit}
          onFollowShop={handleFollowShop}
        />
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="App">
        <Header navLinks={navLinks} />
        <div className="error-state">
          <h2>Error Loading Content</h2>
          <p>{error}</p>
        </div>
        <Footer 
          onEmailSubmit={handleEmailSubmit}
          onFollowShop={handleFollowShop}
        />
      </div>
    );
  }

  return (
    <div className="App">
      <Header navLinks={navLinks} />
      
      {bannerData && (
        <PromotionalBanner 
          message={bannerData.message}
          code={bannerData.code}
          dates={bannerData.dates}
          note={bannerData.note}
          onClick={handleBannerClick}
        />
      )}
      
      <HeroSection 
        imageSrc={heroImageSrc}
      />
      
      <main className="main-content">
        <section className="collections-section" aria-labelledby="collections-heading">
          <div className="collections-intro">
            <span className="section-kicker">Our Cake Edit</span>
            <h2 id="collections-heading">Designed for the way you celebrate</h2>
            <p>
              Explore a curated selection of elegant bakes, from polished centrepieces to
              joyful custom cakes made for intimate moments and grand occasions alike.
            </p>
          </div>

          <div className="products-grid">
            {productData.map((product, index) => (
              <ProductCard key={`${product.title}-${index}`} {...product} />
            ))}
          </div>
        </section>
        
        <section className="about-section">
          <div className="container">
            <div className="about-layout">
              <div className="about-heading">
                <span className="section-kicker">Dublin Home Bakery</span>
                <h2>Quiet luxury, baked with feeling.</h2>
              </div>
              <div className="about-copy">
                <p>
                  Welcome to The Frosting Room, where we transform your cake dreams into delicious reality. 
                  Based in our cozy Dublin home kitchen, we specialize in creating custom cakes that are as 
                  unique as your special moments.
                </p>
                <p>
                  From intimate birthday celebrations to grand wedding receptions, every cake is crafted to be 
                  beautiful to look at and absolutely delightful to taste.
                </p>
              </div>
              <div className="about-highlights" aria-label="The Frosting Room highlights">
                <div>
                  <strong>Custom</strong>
                  <span>Designed around your celebration</span>
                </div>
                <div>
                  <strong>Fresh</strong>
                  <span>Small-batch baking with quality ingredients</span>
                </div>
                <div>
                  <strong>Elegant</strong>
                  <span>Refined finishes without losing warmth</span>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      
      <Footer 
        onEmailSubmit={handleEmailSubmit}
        onFollowShop={handleFollowShop}
      />
    </div>
  );
};

const App: React.FC = () => {
  const [isAdminMode, setIsAdminMode] = useState(false);

  // Check if admin mode is requested via URL parameter
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const adminParam = urlParams.get('admin');
    if (adminParam === 'true') {
      setIsAdminMode(true);
    }
  }, []);

  // If admin mode is active, render admin interface
  if (isAdminMode) {
    return <AdminLayout />;
  }

  return (
    <CookiesProvider>
      <CartProvider>
        <ToastProvider>
          <Router>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/catalog" element={<CatalogPage />} />
              <Route path="/catalog/:collectionName" element={<CollectionDetailPage />} />
              <Route path="/catalog/:collectionName/:productId" element={<ProductDetailPage />} />
              <Route path="/cart" element={<CartPage />} />
              <Route path="/checkout" element={<CheckoutPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/policy" element={<PolicyPage />} />
              <Route path="/faq" element={<FAQPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/cookies" element={<CookiePreferencesPage />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            <CookieConsentBanner />
          </Router>
        </ToastProvider>
      </CartProvider>
    </CookiesProvider>
  );
};

export default App;
